import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import { connect } from 'react-redux';
import { ColorRing } from 'react-loader-spinner';
import { Col } from 'react-bootstrap';
import { postFileRequest, deleteRequest } from '../../requestUtils';
import allActions from '../../actions';
import uploadWhite from '../../../assets/images/upload-white.svg';
import { analyticsTrack } from '../../analyticsUtils';
import './DocumentUpload.scss';

const { profileSuggestions } = allActions;

const renderFileName = (document) => {
  if (document) {
    return document.document_file_name;
  }
  return null;
};

const renderSpinner = (height, width) => (
  <ColorRing
    visible
    height={height}
    width={width}
    ariaLabel="blocks-loading"
    wrapperStyle={{}}
    wrapperClass="updating-icon"
    colors={Array(5).fill('#FFFFFF')}
  />
);

const uploadingText = (documentType) => {
  switch (documentType) {
    case 'resume':
    case 'parsedResume':
      return 'Loading Resume';
    case 'invoice':
      return 'Uploading Receipt';
    case 'job_description':
      return 'Uploading Job Description';
    default:
      return 'Uploading File';
  }
};

class DocumentUpload extends Component {
  constructor(props) {
    super(props);
    this.state = {
      uploading: false,
      document: props.document,
      finishedUploading: false,
      errorMessage: null,
      fileError: false,
    };

    this.uploadResume = this.uploadResume.bind(this);
    this.onUploadDocument = this.onUploadDocument.bind(this);
    this.onUpdateDocument = this.onUpdateDocument.bind(this);
    this.onResumeDrop = this.onResumeDrop.bind(this);
    this.removeDocument = this.removeDocument.bind(this);
    this.renderButtonActions = this.renderButtonActions.bind(this);
    this.renderUploadIcon = this.renderUploadIcon.bind(this);
  }

  onResumeDrop(accepted) {
    this.setState({ uploading: true });
    this.uploadResume(accepted[0]);
  }

  onUploadDocument(prompt) {
    const { location, userId } = this.props;

    analyticsTrack(`Clicked on Add ${prompt}`, {
      location,
      userId,
    });
    this.dropzoneRef.open();
  }

  onUpdateDocument(prompt) {
    const { onboardingQuestionnaire, userId } = this.props;

    analyticsTrack(`Clicked on Update ${prompt}`, {
      location: onboardingQuestionnaire ? 'Onboarding Flow' : 'Consultant Onboarding',
      userId,
    });
    this.dropzoneRef.open();
  }

  uploadResume(file) {
    const {
      uploadDocumentUrl, csrfToken, skipParsing, handleNewResume,
      handleAfterUpload, handleMenuUpdate, userId, documentType,
      handleResumeUpload, location,
    } = this.props;

    this.setState({
      uploading: true, finishedUploading: false, errorMessage: null, fileError: false,
    });
    handleResumeUpload(true);
    analyticsTrack(uploadingText(documentType), { location, userId });
    postFileRequest(uploadDocumentUrl, file, csrfToken, skipParsing).then((response) => {
      if (documentType === 'parsedResume') {
        analyticsTrack('Uploaded Resume', {
          location: 'Onboarding Flow',
          userId,
        });
        const parsed = response.data.parsed_resume;
        const emptyResume = parsed === null || !(
          parsed.education.length || parsed.employments.length
            || parsed.location || parsed.summary
        );
        this.setState(
          {
            document: response.data.resume,
            finishedUploading: true,
            uploading: false,
            fileError: false,
            errorMessage: emptyResume ? 'We were unable to return suggestions. If you have any questions, please reach out to support@clora.com or chat with us below.' : null,
          },
        );
        handleMenuUpdate(true);
        if (!emptyResume) {
          this.props.resumeParsed(parsed);
          handleNewResume(true);
        }
        handleAfterUpload(response.data.resume);
      } else if (documentType === 'job_description') {
        analyticsTrack('Uploaded Job Description', {
          location: 'Onboarding Flow',
          userId,
        });
        handleAfterUpload(response.data.redirect_url);
      } else {
        const document = response.data.resume || response.data;
        this.setState({
          finishedUploading: true,
          uploading: false,
          document,
        });
        handleAfterUpload(document);
      }
      handleResumeUpload(false);
    }, (error) => {
      this.setState({
        document: { document_file_name: file.name },
        finishedUploading: true,
        uploading: false,
        fileError: true,
        errorMessage: error.message,
      });
    });
  }

  removeDocument() {
    const {
      csrfToken, handleMenuUpdate, proposalResourceId, handleNewResume,
    } = this.props;
    const { document } = this.state;

    if (document && Object.keys(document).length > 1) {
      let removeDocumentUrl;
      if (proposalResourceId) {
        removeDocumentUrl = `/proposals/${proposalResourceId}/delete_resume`;
      } else {
        removeDocumentUrl = `/resumes/${document.resource_id}`;
      }
      return deleteRequest(
        removeDocumentUrl,
        csrfToken,
      ).then(() => {
        this.props.reinitializeResume();
        this.setState({ document: null, errorMessage: null });
        handleMenuUpdate(true);
        handleNewResume(false);
      }).catch(() => {});
    }
    this.setState({ document: null, errorMessage: null, fileError: false });

    return null;
  }

  renderResumeAction(document, uploading, finishedUploading, prompt, buttonStyle) {
    if (document && !uploading) {
      return this.renderUploadIcon();
    } if (uploading && !finishedUploading) {
      return (
        <a
          role="button"
          tabIndex={0}
          className="link document-upload"
          onClick={() => { this.dropzoneRef.open(); }}
        >
          { renderSpinner(30, 30) }
        </a>
      );
    }
    return (
      <a
        role="button"
        tabIndex={0}
        className={`${buttonStyle} document-upload`}
        onClick={() => this.onUploadDocument(prompt)}
      >
        <span className="medium-weight">
          <i className="fa fa-plus" />
          {prompt}
        </span>
      </a>
    );
  }

  renderButtonActions() {
    const { uploading } = this.state;
    const { prompt, documentType } = this.props;

    if (uploading) {
      return (
        <div className="uploading-container">
          { renderSpinner(50, 60) }
          <a
            role="button"
            tabIndex={0}
            className="btn btn-primary"
            onClick={() => this.onUploadDocument(prompt)}
          >
            {`${uploadingText(documentType)}...`}
          </a>
        </div>
      );
    }
    return (
      <div className="upload-button-container">
        <div className="document-icon">
          {this.renderUploadIcon()}
        </div>
        <a
          role="button"
          tabIndex={0}
          className="btn btn-primary"
          onClick={() => this.onUploadDocument(prompt)}
        >
          {prompt}
        </a>
      </div>
    );
  }

  renderUploadIcon() {
    const { fileError } = this.state;
    const { prompt } = this.props;
    return (
      <button
        tabIndex={0}
        className={`${fileError ? 'update-document-error' : 'update-document'}`}
        onClick={() => this.onUpdateDocument(prompt)}
      >
        <img src={uploadWhite} alt="upload-document-icon" />
      </button>
    );
  }

  render() {
    const {
      finishedUploading, uploading, document, errorMessage, fileError,
    } = this.state;
    const {
      viewDocumentUrl, prompt, buttonStyle, documentType,
    } = this.props;

    return (
      <Fragment>
        <Col className="ps-0">
          <div className="document-upload new-design">
            <Dropzone
              ref={(node) => { this.dropzoneRef = node; }}
              className="document-dropzone"
              name="document-upload"
              multiple={false}
              onDrop={this.onResumeDrop}
            />
            <div className="mb-32">
              { document
                ? (
                  <div>
                    <div className={`${document && 'g-row--always g-row--between'} ${fileError ? 'file-name-error' : 'file-name'}`}>
                      <div className={`${fileError ? 'document-icon-error' : 'document-icon'}`}>
                        {this.renderResumeAction(
                          document,
                          uploading,
                          finishedUploading,
                          prompt,
                          `${buttonStyle} ${buttonStyle === 'button' && 'btn btn-primary'}`,
                        )}
                      </div>
                      <a className="body-text-2 document-name" href={viewDocumentUrl} target="_blank" rel="noreferrer">
                        {renderFileName(document)}
                      </a>
                      {(documentType === 'resume' || documentType === 'parsedResume')
                    && (
                    <button
                      className={`${fileError ? 'remove-document-error' : 'remove-document'}`}
                      onClick={this.removeDocument}
                    >
                      <i className="fas fa-trash-alt" />
                    </button>
                    )}
                    </div>
                  </div>
                )
                : this.renderButtonActions()}
            </div>
          </div>
        </Col>
        <Col>
          <div className={`mt-8 ${(errorMessage && fileError) && 'error-message'}`}>
            {(errorMessage && !fileError) && <i className="me-3 fas fa-exclamation-triangle" />}
            {errorMessage}
          </div>
        </Col>
      </Fragment>
    );
  }
}

DocumentUpload.propTypes = {
  document: PropTypes.shape({}),
  proposalResourceId: PropTypes.string,
  prompt: PropTypes.oneOf([PropTypes.string, PropTypes.node]),
  viewDocumentUrl: PropTypes.string,
  uploadDocumentUrl: PropTypes.string,
  csrfToken: PropTypes.string,
  buttonStyle: PropTypes.string,
  resumeParsed: PropTypes.func,
  reinitializeResume: PropTypes.func,
  handleMenuUpdate: PropTypes.func,
  handleAfterUpload: PropTypes.func,
  userId: PropTypes.string,
  documentType: PropTypes.oneOf(
    ['resume', 'parsedResume', 'invoice', 'job_description'],
  ),
  handleNewResume: PropTypes.func,
  handleResumeUpload: PropTypes.func,
  skipParsing: PropTypes.bool,
  location: PropTypes.string.isRequired,
  onboardingQuestionnaire: PropTypes.string,
};

DocumentUpload.defaultProps = {
  document: null,
  proposalResourceId: null,
  prompt: 'Add File',
  buttonStyle: 'link',
  viewDocumentUrl: null,
  uploadDocumentUrl: '',
  csrfToken: '',
  userId: '',
  documentType: '',
  handleAfterUpload: () => {},
  handleMenuUpdate: () => {},
  handleNewResume: () => {},
  handleResumeUpload: () => {},
  reinitializeResume: () => {},
  resumeParsed: () => {},
  skipParsing: false,
  onboardingQuestionnaire: false,
};

const mapDispatchToProps = (dispatch) => ({
  resumeParsed: (data) => dispatch(profileSuggestions.resumeParsed(data)),
  reinitializeResume: () => dispatch(profileSuggestions.reinitializeResume()),
});

export default connect(null, mapDispatchToProps)(DocumentUpload);
